import type { CampaignManagementApi } from '~/services/campaign-management-api/campaign-management-api.types';

import type {
  PartnerActiveCampaign,
  PartnerCampaignFilterOption,
  PartnerHistoryItem,
  PartnerPerformance,
  PartnerViewItem,
  RevalidaPartner,
} from '../types';

import { formatCampaignDate, formatPct, formatTargetName } from '../utils';
import { mapApiCampaignStatus } from './campaignMappers';

/* eslint-disable perfectionist/sort-modules -- helpers must precede consumers */

type TargetSearchParams = Pick<
  CampaignManagementApi.ListTargetsParams,
  'campaign_name' | 'cnpj' | 'target_id'
>;

function buildJourneyDetail(item: {
  revalidated_count: number;
  revalidation_rate: number;
}): null | string {
  if (item.revalidated_count === 0) return null;

  return `revalidou ${item.revalidated_count} instalações (taxa ${formatPct(item.revalidation_rate)})`;
}

function isJourneyItemRemoved(
  item: Pick<
    CampaignManagementApi.TargetCampaignJourneyHistoryItem,
    'campaign_status' | 'removal_reason' | 'removed_at' | 'target_status'
  >,
): boolean {
  if (item.removed_at || item.removal_reason) return true;

  const targetStatus = item.target_status.toUpperCase();

  if (targetStatus.includes('REMOV') || targetStatus.includes('REJECT')) return true;
  if (item.campaign_status === 'CANCELED') return true;

  return false;
}

function isJourneyItemCurrent(
  item: CampaignManagementApi.TargetCampaignJourneyHistoryItem,
  activeCampaignId?: string,
): boolean {
  if (activeCampaignId && item.campaign_id === activeCampaignId) return true;

  if (isJourneyItemRemoved(item)) return false;

  return item.campaign_status !== 'FINISHED' && item.campaign_status !== 'CANCELED';
}

function mapJourneyItemToTimelineEvents(
  item: CampaignManagementApi.TargetCampaignJourneyHistoryItem,
  activeCampaignId?: string,
): PartnerHistoryItem[] {
  const isCurrent = isJourneyItemCurrent(item, activeCampaignId);
  const isRemoved = isJourneyItemRemoved(item);
  const startDate = formatCampaignDate(item.entered_at);

  const entryEvent: PartnerHistoryItem = {
    campaignName: item.campaign_name,
    detail: null,
    endDate: null,
    id: `${item.campaign_id}-entered`,
    startDate,
    type: isCurrent ? 'active' : 'entered',
  };

  if (isCurrent) {
    return [entryEvent];
  }

  if (isRemoved) {
    return [
      {
        campaignName: item.campaign_name,
        detail: item.removal_reason,
        endDate: item.removed_at ? formatCampaignDate(item.removed_at) : null,
        id: `${item.campaign_id}-removed`,
        startDate,
        type: 'removed',
      },
      entryEvent,
    ];
  }

  return [
    {
      campaignName: item.campaign_name,
      detail: buildJourneyDetail(item),
      endDate: formatCampaignDate(item.campaign_ends_at),
      id: `${item.campaign_id}-completed`,
      startDate,
      type: 'completed',
    },
    entryEvent,
  ];
}

export function mapCampaignTargetToPartnerPerformance(
  item: CampaignManagementApi.CampaignTargetItem,
): PartnerPerformance {
  return {
    eligible: item.enrolled_count,
    id: item.target_id,
    name: `Parceiro ${item.target_id}`,
    pct: item.revalidation_rate ?? null,
    revalidated: item.revalidated_count,
    volume: '—',
  };
}

export function mapCampaignTargetsResponse(data: CampaignManagementApi.CampaignTargetsData) {
  const partners = data.targets
    .map(mapCampaignTargetToPartnerPerformance)
    .sort((left, right) => right.revalidated - left.revalidated);

  return {
    page: data.pagination.page,
    pages: data.pagination.total_pages,
    partners,
    total: data.pagination.total_items,
  };
}

export function mapJourneyActiveCampaign(
  activeCampaign: CampaignManagementApi.TargetCampaignJourneyActiveCampaign,
): PartnerActiveCampaign {
  return {
    campaignId: activeCampaign.campaign_id,
    campaignName: activeCampaign.campaign_name,
    eligibleCount: activeCampaign.enrolled_count,
    incentiveType: activeCampaign.incentive_name,
    joinedAt: formatCampaignDate(activeCampaign.entered_at),
    leftAt: activeCampaign.removed_at
      ? formatCampaignDate(activeCampaign.removed_at)
      : null,
    revalidatedCount: activeCampaign.revalidated_count,
    revalidatedPct: activeCampaign.revalidation_rate,
    status: mapApiCampaignStatus(activeCampaign.campaign_status),
  };
}

export function mapJourneySummaryToPartner(
  targetId: string,
  summary: CampaignManagementApi.TargetCampaignJourneySummary,
): RevalidaPartner {
  return {
    id: targetId,
    name: `Parceiro ${targetId}`,
    revalidatedPct: summary.total_revalidation_rate,
    revalidatedTotal: summary.total_revalidated_count,
    totalEligibleHistorical: summary.total_enrolled_count,
  };
}

export function mapTargetCampaignJourneyResponse(
  data: CampaignManagementApi.TargetCampaignJourneyData,
) {
  const activeCampaignId = data.active_campaign?.campaign_id;

  return {
    activeCampaign: data.active_campaign
      ? mapJourneyActiveCampaign(data.active_campaign)
      : null,
    history: data.journey_history.items.flatMap((item) => mapJourneyItemToTimelineEvents(item, activeCampaignId)),
    partner: mapJourneySummaryToPartner(data.target_id, data.summary),
  };
}

export function mapTargetItem(item: CampaignManagementApi.TargetListItem): PartnerViewItem {
  return {
    activeCampaignCode: item.active_campaign?.campaign_code ?? null,
    activeCampaignId: item.active_campaign?.campaign_id ?? null,
    activeCampaignName: item.active_campaign?.campaign_name ?? null,
    eligibleCount: item.enrolled_count,
    id: item.target_id,
    name: formatTargetName(item.target_name),
    revalidatedCount: item.revalidated_count,
    revalidatedPct: item.revalidation_rate ?? null,
  };
}

export function mapTargetListResponse(data: CampaignManagementApi.ListTargetsData) {
  return {
    page: data.page,
    pages: data.pages,
    partners: data.items.map(mapTargetItem),
    total: data.total,
  };
}

export function extractCampaignFilterOptionsFromPartners(
  partners: PartnerViewItem[],
): PartnerCampaignFilterOption[] {
  const byId = new Map<string, PartnerCampaignFilterOption>();

  for (const partner of partners) {
    const { activeCampaignId, activeCampaignName } = partner;

    if (activeCampaignId && activeCampaignName) {
      byId.set(activeCampaignId, { id: activeCampaignId, label: activeCampaignName });
    }
  }

  return [...byId.values()];
}

export function mergeCampaignFilterOptions(
  existing: PartnerCampaignFilterOption[],
  incoming: PartnerCampaignFilterOption[],
): PartnerCampaignFilterOption[] {
  const byId = new Map(existing.map((option) => [option.id, option]));

  for (const option of incoming) {
    byId.set(option.id, option);
  }

  return [...byId.values()].sort((left, right) => left.label.localeCompare(right.label, 'pt-BR'));
}

export function mapTargetToActiveCampaign(
  item: CampaignManagementApi.TargetListItem,
  incentiveName?: string,
): null | PartnerActiveCampaign {
  const activeCampaign = item.active_campaign;
  if (!activeCampaign) return null;

  return {
    campaignId: activeCampaign.campaign_id,
    campaignName: activeCampaign.campaign_name,
    eligibleCount: item.enrolled_count,
    incentiveType: incentiveName ?? '—',
    joinedAt: formatCampaignDate(activeCampaign.entered_at),
    leftAt: null,
    revalidatedCount: item.revalidated_count,
    revalidatedPct: item.revalidation_rate ?? 0,
    status: mapApiCampaignStatus(activeCampaign.campaign_status),
  };
}

export function mapTargetToPartnerSummary(
  item: CampaignManagementApi.TargetListItem,
): RevalidaPartner {
  return {
    id: item.target_id,
    name: formatTargetName(item.target_name),
    revalidatedPct: item.revalidation_rate ?? null,
    revalidatedTotal: item.revalidated_count,
    totalEligibleHistorical: item.enrolled_count,
  };
}

export function parsePartnerSearchParams(search: string): TargetSearchParams {
  const query = search.trim();
  if (!query) return {};

  const digits = query.replace(/\D/g, '');

  if (digits.length === 14) {
    return { cnpj: digits };
  }

  if (query.length >= 3 && /[a-zA-ZÀ-ú]/.test(query)) {
    return { campaign_name: query };
  }

  return { target_id: query };
}
